import React, { useEffect, useState } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import pluralize from "pluralize";
import { isEqual } from "lodash";

import { useAppState } from "core/contexts/AppContext";
import { useCountryAndCurrency } from "core/hooks/useCountryAndCurrency";

import HeaderContainer from "pages/Components/CartAndCheckout/components/HeaderContainer";
import HeaderTitle from "pages/Components/CartAndCheckout/components/HeaderTitle";
import HeaderCurrency from "pages/Components/CartAndCheckout/components/HeaderCurrency";
import SummaryRow from "pages/Components/CartAndCheckout/components/Summary/SummaryRow";
import TitleAndPrice from "pages/Components/CartAndCheckout/components/TitleAndPrice";
import SummaryContainer from "pages/Components/CartAndCheckout/components/Summary/SummaryContainer";
import useCartDetailSizes from "pages/Components/CartAndCheckout/hooks/useCartDetailSizes";
import SummaryAndButtonContainer from "pages/Components/CartAndCheckout/components/Summary/SummaryAndButtonContainer";
import SummaryRowsContainer from "pages/Components/CartAndCheckout/components/Summary/SummaryRowsContainer";
import SummaryItemsContainer from "pages/Components/CartAndCheckout/components/Summary/SummaryItemsContainer";
import HeaderItems from "pages/Components/CartAndCheckout/components/Summary/HeaderItems";
import SummaryItemRow from "pages/Components/CartAndCheckout/components/Summary/SummaryItemRow";
import SummaryItemRowsContainer from "pages/Components/CartAndCheckout/components/Summary/SummaryItemRowsContainer";
import Description from "pages/Components/CartAndCheckout/components/Description";
import useCart from "pages/Components/CartPage/hooks/useCart";

type Props = {
    isCheckout?: boolean,
    showShippingDisclaimer?: boolean,
    productDisclaimer?: string,
    cartStatus?: string,
    children?: React.ReactNode,
}

type STATE = {
    taxAmount: number,
    totalAmount: number,
    subtotalAmount: number,
    cartProducts: Array<CartProduct>,
    itemCount: number,
    shippingAmount: number,
}

const INITIAL_STATE = {
    taxAmount: 0,
    totalAmount: 0,
    subtotalAmount: 0,
    cartProducts: [],
    itemCount: 0,
    shippingAmount: 0,
};

const CartSummaryContainer: React.FC<Props> = (
    {
        isCheckout = false,
        showShippingDisclaimer = false,
        productDisclaimer = null,
        cartStatus = '',
        children = null,
    }
) => {
    const [state, setState] = useState<STATE>(INITIAL_STATE);
    const theme: Theme = useTheme();
    const { itemGroupBoxWidth } = useCartDetailSizes();
    const [appState] = useAppState();
    const { getCountry } = useCart();
    const countryCode = getCountry();

    const isGlobal = appState.globalCurrencyOptions.isGlobal || appState.cart?.isGlobal;
    const { currency } = useCountryAndCurrency(countryCode.code);

    const isEmptyCart = cartStatus === 'empty' || !state.itemCount;

    // Set state so information is retained when cart is cleared after purchase.
    useEffect(() => {
        if (!appState.cart) return;
        const cartProducts = appState.cart.products || [];

        const taxAmount = appState.cart.taxAmount;
        const totalAmount = appState.cart.totalAmount;
        const subtotalAmount = appState.cart.subtotalAmount;
        const itemCount = cartProducts.length;
        const shippingAmount = appState.cart.shippingAmount;

        const newState = {
            taxAmount,
            totalAmount,
            itemCount,
            cartProducts,
            subtotalAmount,
            shippingAmount,
        };

        if (isEqual(state, newState)) return;

        setState(newState);
    }, [state, appState.cart]);

    const isFirstDiscounted = !!state.cartProducts?.[0]?.discountedPrice;

    const messageStyle = css`
        margin-top: 38px;
        font-size: 20px;
        line-height: 23px;
        text-align: center;

        ${theme.breakpoints.between('sm', 'xl')} {
            margin-top: 36px;
            font-size: 15px;
            line-height: 18px;
        }

        ${theme.breakpoints.down('md')} {
            margin-top: 26px;
        }

        ${theme.breakpoints.down('xs')} {
            font-size: 14px;
            line-height: 17px;
        }
    `;

    const buttonPlaceholderStyle = css`
        height: 103px;
    `;

    const summaryContainerStyle = css`
        ${isFirstDiscounted && css`
            margin-top: 18px;
        `}
    `;

    return (
        <SummaryContainer itemGroupBoxWidth={itemGroupBoxWidth}>
            <HeaderContainer alignItem={'end'} borderColour={'white'}>
                <HeaderTitle textColour={'white'}>Order Summary</HeaderTitle>
                <HeaderCurrency>Price in {currency}</HeaderCurrency>
            </HeaderContainer>

            {isEmptyCart ? (
                <div css={messageStyle}>Nothing to show here right now.</div>
            ) : (
                <>
                    {isCheckout && (
                        <SummaryItemsContainer>
                            <HeaderItems>{pluralize('item', state.itemCount, true)}</HeaderItems>
                            <SummaryItemRowsContainer css={summaryContainerStyle}>
                                {state.cartProducts.map((item: CartProduct, index: number) => (
                                    <SummaryItemRow
                                        item={item}
                                        key={item.title}
                                        isLast={index === state.itemCount - 1}
                                    />
                                ))}
                            </SummaryItemRowsContainer>
                        </SummaryItemsContainer>
                    )}

                    <SummaryAndButtonContainer>
                        <SummaryRowsContainer>
                            <SummaryRow type={isCheckout ? 'shipping' : 'subTotal'}>
                                {isCheckout && (
                                    <TitleAndPrice
                                        title={'Shipping fee'}
                                        price={state.shippingAmount ?? 0}
                                    />
                                )}
                                {isCheckout && showShippingDisclaimer && (
                                    <Description marginTop={10}>
                                        Shipping fees shown are estimates only. For orders outside major metropolitan areas, actual shipping costs may vary and will be confirmed prior to invoicing.
                                    </Description>
                                )}
                                {(!isGlobal || (isGlobal && isCheckout)) && (
                                    <TitleAndPrice
                                        title={'Subtotal'}
                                        price={state.subtotalAmount}
                                    />
                                )}
                                {!isGlobal && (
                                    <TitleAndPrice
                                        title={
                                            isCheckout ?
                                                `Total ${countryCode.taxLabel}` :
                                                countryCode.taxLabel
                                        }
                                        price={state.taxAmount}
                                    />
                                )}
                            </SummaryRow>

                            <SummaryRow type={'total'}>
                                <TitleAndPrice
                                    title={'Total cost'}
                                    price={state.totalAmount}
                                    isTotal
                                />
                            </SummaryRow>
                        </SummaryRowsContainer>

                        {children ?? <div css={buttonPlaceholderStyle} />}
                    </SummaryAndButtonContainer>

                    {productDisclaimer && (
                        <Description marginTop={30} textAlign={'center'}>
                            {productDisclaimer}
                        </Description>
                    )}
                </>
            )}

        </SummaryContainer>
    );
}

export default CartSummaryContainer;